import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { StarBadge } from '@/components/Lantern';
import { Mark } from '@/components/Mark';
import { Sky } from '@/components/Sky';
import { TickIcon } from '@/components/Icons';
import { fromDateKey, isRecordableDay } from '@/domain/date';
import type { Mark as MarkValue } from '@/domain/prayer';
import {
  EMPTY_MARK,
  QUALITIES,
  isPrayerId,
  isQualityEnabled,
  normaliseMark,
  toggleQuality,
} from '@/domain/prayer';
import { markFor } from '@/domain/streaks';
import { useAppState } from '@/state/useAppState';
import { useDayRecord } from '@/state/useDayRecord';
import { useT } from '@/state/useT';

const TINTS = ['var(--q1)', 'var(--q2)', 'var(--q3)', 'var(--q4)'] as const;
const TINT_BGS = ['var(--q1-12)', 'var(--q2-12)', 'var(--q3-12)', 'var(--q4-12)'] as const;

/** How far a press has to travel before it is a drag rather than a tap. */
const DRAG_START = 8;
/** How far down the sheet has to be dragged before letting go dismisses it. */
const DISMISS_DISTANCE = 96;
/** Or how fast, so a short flick counts as well as a long drag. */
const DISMISS_VELOCITY = 0.5;
/** Roughly a frame: below this the clock cannot say how fast anything moved. */
const MIN_FLICK_MS = 16;
const MIN_FLICK_PX = 24;
/** Long enough to read as the sheet going down; matched by `.sheet.out`. */
const OUT_MS = 200;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function LogPrayer() {
  const { prayerId } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const { skin, times, timesProvider, today } = useAppState();
  const { t, time, dayLabel } = useT();
  const navigate = useNavigate();

  // A malformed key, or one naming a day nobody can have prayed in yet, is not
  // a day to write to — fall back to today rather than opening a sheet over
  // nothing. Records can be filled in behind, never ahead.
  const asked = params.get('date');
  const date = isRecordableDay(asked, today) ? asked : today;

  const { day, loading, setMark } = useDayRecord(date);
  const [draft, setDraft] = useState<MarkValue>(EMPTY_MARK);
  const [hydrated, setHydrated] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [closing, setClosing] = useState(false);

  const sheet = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ id: number; y: number; at: number; offset: number } | null>(null);
  /** A drag ends in a click too, and only a real tap should count as one. */
  const dragged = useRef(false);
  const leaving = useRef(false);
  const exit = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const valid = typeof prayerId === 'string' && isPrayerId(prayerId);

  /**
   * Where leaving goes. Whoever opened the sheet says so, because the answer
   * differs: Home for a prayer row, and the report — on the very window it was
   * paged to — for one of its cells. A cold deep-link carries no state and
   * lands on Home, which is the one place that is always there.
   */
  const back = (location.state as { from?: string } | null)?.from ?? '/';

  // `day.date === date` and not `loading` alone: on the render where `date`
  // changes — the sheet left open across Fajr, when today's key rolls over —
  // the record hook has not run its own effect yet, so `loading` still reads
  // false and `day` still holds the day before. Hydrating there seeded the
  // draft from yesterday's marks and then pinned `hydrated`, so it never
  // corrected itself and Save wrote them onto the new day.
  useEffect(() => {
    if (!valid || loading || day.date !== date || hydrated === date) return;
    setDraft(markFor(day, prayerId));
    setHydrated(date);
  }, [valid, loading, hydrated, date, day, prayerId]);

  // Today's times are memoised in the provider; another day has to be worked
  // out, so a prayer filled in last week shows the adhān it actually had.
  const dayTimes = useMemo(
    () => (date === today ? times : timesProvider.forDate(date)),
    [date, today, times, timesProvider],
  );

  const close = useCallback(() => {
    // A scrim tap and an Escape can land together, and two navigations would
    // pop one screen too many.
    if (leaving.current) return;
    leaving.current = true;
    if (prefersReducedMotion()) {
      void navigate(back, { replace: true });
      return;
    }
    setClosing(true);
    exit.current = window.setTimeout(() => void navigate(back, { replace: true }), OUT_MS);
  }, [back, navigate]);

  useEffect(
    () => () => {
      if (exit.current !== null) window.clearTimeout(exit.current);
    },
    [],
  );

  // Escape is what a keyboard reaches for, and the sheet is a dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  // The sheet arrives over another screen, so focus has to come with it or a
  // screen reader goes on reading the page underneath.
  useEffect(() => {
    sheet.current?.focus({ preventScroll: true });
  }, []);

  /**
   * The drag. The offset is written straight to the element rather than held
   * in state: a re-render per pointer frame is the one thing that makes a
   * gesture feel like it is not tracking the finger.
   */
  const offsetTo = (px: number) => {
    const el = sheet.current;
    if (el) el.style.setProperty('--drag', `${px}px`);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (closing || e.button !== 0) return;
    // Clear it here rather than trusting the click to. The gesture is on the
    // sheet head as well as the grip, and a drag on the head produces no click
    // on the grip to reset it — so the flag survived and swallowed the next
    // real tap on the grip instead of the drag it was set for.
    dragged.current = false;
    drag.current = { id: e.pointerId, y: e.clientY, at: e.timeStamp, offset: 0 };
    // jsdom has no pointer capture, and the gesture reads fine without it.
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    // Down only: a sheet already against the bottom of the screen has nowhere
    // to go upwards.
    d.offset = Math.max(0, e.clientY - d.y);
    if (d.offset > DRAG_START) setDragging(true);
    offsetTo(d.offset);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (d.offset <= DRAG_START) {
      // A tap, not a drag: the grip's own click answers it.
      offsetTo(0);
      return;
    }
    dragged.current = true;
    // The velocity test needs an interval long enough to measure and a
    // distance worth measuring over. Under a frame's worth of time, any
    // movement at all divides out as an impossibly fast flick — which is how a
    // deliberate 30px nudge came to dismiss the sheet instead of snapping it
    // back. Below that floor, distance decides on its own.
    const elapsed = e.timeStamp - d.at;
    const flicked =
      elapsed >= MIN_FLICK_MS && d.offset > MIN_FLICK_PX && d.offset / elapsed > DISMISS_VELOCITY;
    // Leave the offset where the finger left it when it is going: the exit
    // then carries on from there rather than snapping back up first.
    if (d.offset > DISMISS_DISTANCE || flicked) close();
    else offsetTo(0);
  };

  const gesture = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
  };

  const onGripClick = () => {
    if (dragged.current) {
      dragged.current = false;
      return;
    }
    close();
  };

  if (!valid) return <Navigate to="/" replace />;

  const kids = skin === 'kids';
  const name = t(`prayer.${prayerId}`);
  const script = t(`prayer.ar.${prayerId}`);
  const allFour = draft.prayed && draft.onTime && draft.jamaah && draft.takbirah;

  const commit = async (mark: MarkValue) => {
    try {
      await setMark(prayerId, normaliseMark(mark));
    } catch {
      // Leaving the sheet open keeps what they chose on screen, so a second
      // try does not start from nothing.
      setFailed(true);
      return;
    }
    void navigate(back, { replace: true });
  };

  return (
    <div className="screen">
      <Sky />
      {/* Pointer-only, and deliberately not a button: `aria-modal` below tells
          a screen reader to ignore everything outside the sheet, so a control
          out here would be announced to nobody. The grip inside it is the one
          that carries the name. */}
      <div className="sheet-scrim" data-testid="sheet-scrim" onClick={close} />
      <div
        className={`sheet${closing ? ' out' : ''}${dragging ? ' dragging' : ''}`}
        ref={sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        tabIndex={-1}
      >
        {/* The grip is both the gesture and the button: a 5px bar is not a
            target, and a drag is not something a keyboard can perform. */}
        <button
          type="button"
          className="sheet-grab"
          data-testid="sheet-grip"
          aria-label={t('log.close')}
          onClick={onGripClick}
          {...gesture}
        >
          <span className="sheet-grip" aria-hidden="true" />
        </button>

        <div className="sheet-head" {...gesture}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Mark mark={draft} size={62} />
            <div style={{ flexGrow: 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
                <h1
                  id="sheet-title"
                  style={{
                    fontFamily: 'var(--fd)',
                    fontSize: 25,
                    fontWeight: 800,
                    lineHeight: 1.2,
                    margin: 0,
                  }}
                >
                  {name}
                </h1>
                {script ? (
                  <div
                    lang="ar"
                    dir="rtl"
                    style={{
                      fontFamily: 'var(--fa)',
                      fontSize: 18,
                      lineHeight: 1.2,
                      color: 'var(--accent-ink)',
                    }}
                  >
                    {script}
                  </div>
                ) : null}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.3,
                  color: 'var(--panel-dim)',
                  fontWeight: 600,
                  marginTop: 3,
                }}
              >
                {t('log.adhan', { time: time(dayTimes[prayerId]) })}
              </div>
              {/* Which day is being written to, whenever it is not this one.
                A sheet that looks identical for every day in the record is
                one nobody can check before saving. */}
              {date !== today ? (
                <div
                  data-testid="sheet-day"
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.3,
                    color: 'var(--accent-ink)',
                    fontWeight: 700,
                    marginTop: 2,
                  }}
                >
                  {t('log.onDay', { day: dayLabel(fromDateKey(date)) })}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* The body scrolls, so a tall sheet — four toggles, the reward card,
            two buttons — is reachable on a short screen and in Arabic, where
            every line of it is taller. */}
        <div className="sheet-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {QUALITIES.map((q, i) => {
              const enabled = isQualityEnabled(draft, q.key);
              const on = draft[q.key];
              const script2 = t(`quality.ar.${q.key}`);
              return (
                <button
                  key={q.key}
                  type="button"
                  data-testid={`quality-${q.key}`}
                  className={`trow${on ? ' on' : ''}`}
                  aria-disabled={!enabled}
                  aria-pressed={on}
                  style={{ '--tint': TINTS[i], '--tintbg': TINT_BGS[i] } as React.CSSProperties}
                  onClick={() => enabled && setDraft((d) => toggleQuality(d, q.key))}
                >
                  <span className="box">
                    <TickIcon />
                  </span>
                  <span style={{ flexGrow: 1 }}>
                    <span className="ttitle">{t(`quality.${q.key}.label`)}</span>
                    <span className="tsub">
                      {q.key === 'takbirah' && !draft.jamaah
                        ? t('quality.needsJamaah')
                        : t(`quality.${q.key}.${kids ? 'kids' : 'adult'}`)}
                    </span>
                  </span>
                  {script2 ? (
                    <span
                      lang="ar"
                      dir="rtl"
                      style={{
                        fontFamily: 'var(--fa)',
                        fontSize: 15,
                        lineHeight: 1.2,
                        color: 'var(--panel-dim)',
                      }}
                    >
                      {script2}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {allFour ? (
            <div
              className="pop reward kids-only"
              style={{
                marginTop: 13,
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <StarBadge size={34} />
              <div style={{ flexGrow: 1 }}>
                <div
                  style={{
                    fontFamily: 'var(--fd)',
                    fontSize: 17,
                    fontWeight: 800,
                    lineHeight: 1.25,
                  }}
                >
                  {t('log.allFour.title')}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    lineHeight: 1.3,
                    color: 'var(--panel-dim)',
                    fontWeight: 600,
                  }}
                >
                  {t('log.allFour.sub', { prayer: name })}
                </div>
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: 15, display: 'flex', flexDirection: 'column', gap: 9 }}>
            <button type="button" className="btn-p" onClick={() => void commit(draft)}>
              {t('log.save')}
            </button>
            {/* Making one up IS praying it, just not in its time — so it records
              as prayed without the on-time quality, rather than as a miss. */}
            <button
              type="button"
              className="btn-g"
              onClick={() => void commit({ ...draft, prayed: true, onTime: false })}
            >
              {t(kids ? 'log.makeUp.kids' : 'log.makeUp.adult')}
            </button>
          </div>

          {failed ? (
            <div
              data-testid="save-failed"
              role="alert"
              style={{
                marginTop: 12,
                fontSize: 12.5,
                lineHeight: 1.4,
                fontWeight: 700,
                color: 'var(--q4)',
                textAlign: 'center',
              }}
            >
              {t('log.saveFailed')}
            </div>
          ) : null}

          <div
            style={{
              marginTop: 12,
              fontSize: 11.5,
              lineHeight: 1.4,
              color: 'var(--panel-dim)',
              textAlign: 'center',
            }}
          >
            {t('log.privacy')}
          </div>
        </div>
      </div>
    </div>
  );
}
